import React, { useState, useEffect, useCallback } from "react";
import { DollarSign } from "lucide-react";
import { getCommissionsInfluenceur } from "../../services/InfluenceurService";
import * as LoadingIcons from "react-loading-icons";

// Format date + heure
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString("fr-FR", options);
    const timePart = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `${datePart} à ${timePart}`;
};

// Traduire type d'abonnement
const formatAbonnement = (type) => {
    if (!type) return "-";
    return type === "monthly" ? "Mensuel" : type === "yearly" ? "Annuel" : type;
};

const CommissionsInfluenceur = () => {
    const [paiements, setPaiements] = useState([]);
    const [totalCommission, setTotalCommission] = useState(0);
    const [commissionPro, setCommissionPro] = useState(0);
    const [commissionBusiness, setCommissionBusiness] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });

    // Détecte si mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // fetchPaiements encapsulé avec useCallback
    const fetchPaiements = useCallback(async (pageNumber = 1) => {
        setLoading(true);
        try {
            const perPage = isMobile ? 12 : 10;
            const data = await getCommissionsInfluenceur(pageNumber, perPage);

            setPaiements(data.paiements);
            setTotalCommission(parseFloat(data.total_commission));
            setCommissionPro(parseFloat(data.commission_pro));
            setCommissionBusiness(parseFloat(data.commission_business));
            setPagination({
                current_page: data.current_page,
                last_page: data.last_page,
                per_page: data.per_page,
                total: data.total,
            });
        } catch (err) {
            console.error("Erreur récupération commissions:", err.message);
            setPaiements([]);
            setTotalCommission(0);
            setCommissionPro(0);
            setCommissionBusiness(0);
        } finally {
            setLoading(false);
        }
    }, [isMobile]);

    // useEffect pour récupérer les paiements
    useEffect(() => {
        fetchPaiements(page);
    }, [page, fetchPaiements]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            setPage(newPage);
        }
    };

    const stats = [
        { title: "Pro", value: commissionPro, color: "bg-blue-100 text-blue-600" },
        { title: "Business", value: commissionBusiness, color: "bg-purple-100 text-purple-600" },
        { title: "Total", value: totalCommission, color: "bg-yellow-100 text-yellow-600" },
    ];

    return (
        <div className="w-full flex justify-center pb-20 lg:mb-0 mb-16 font-Poppins">
            <div className="w-full max-w-6xl space-y-6">
                <h1 className="text-2xl lg:text-3xl font-extrabold text-start text-color6 mb-6">
                    📊 Commissions
                </h1>

                {/* KPI Commissions */}
                <div className="grid grid-cols-3 gap-4 lg:gap-6">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col items-center justify-between bg-white shadow rounded-2xl p-2 lg:p-3"
                        >
                            <div className={`p-2 lg:p-3 rounded-full ${stat.color}`}>
                                <DollarSign size={20} className="xl:size-10 lg:size-10" />
                            </div>
                            <div className="text-center mt-1">
                                <p className="text-xs lg:text-xl text-gray-800 font-semibold lg:font-semibold">{stat.title}</p>
                                <h3 className="text-sm lg:text-3xl font-extrabold font-atkinson">
                                    {stat.value > 0 ? stat.value.toFixed(2) + " €" : "-"}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tableau des paiements */}
                <div className="bg-white shadow rounded-xl w-full overflow-x-auto">
                    <table className="w-full min-w-max border-collapse text-center text-sm sm:text-base lg:text-lg">
                        <thead className="bg-color1 text-white sticky top-0">
                            <tr>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Nom</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Statut</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Abonnement</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Date paiement</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Prix plan (€)</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:p-4 lg:font-semibold font-medium lg:text-lg text-xs">Commission (€)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="lg:h-[400px] h-[300px] text-center">
                                        <div className="flex justify-center items-center">
                                            <LoadingIcons.SpinningCircles
                                                stroke="#2218eeff"
                                                fill="#2218eeff"
                                                strokeWidth={3}
                                                height={30}
                                                width={30}
                                            />
                                            <span className="ml-2 text-gray-600 font-medium text-sm sm:text-base">Chargement...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : paiements.length > 0 ? (
                                paiements.map((p) => (
                                    <tr key={p.id} className="border-b hover:bg-gray-50">
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{p.user_nom || "–"}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">
                                            <span className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-sm lg:text-base rounded-full ${p.user_statut === "basic" ? "bg-gray-700 text-white" :
                                                p.user_statut === "pro" ? "bg-blue-700 text-white" : "bg-purple-700 text-white"
                                                }`}>
                                                {p.user_statut}
                                            </span>
                                        </td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{formatAbonnement(p.user_type_abonnement)}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{formatDate(p.date_paiement)}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{parseFloat(p.montant).toFixed(2)} €</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-semibold text-green-700 text-xs sm:text-sm lg:text-base">
                                            {p.commission > 0 ? parseFloat(p.commission).toFixed(2) + " €" : "-"}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="py-2 text-center text-gray-500 text-xs sm:text-sm lg:text-base">Aucun paiement trouvé.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex w-full justify-center items-center gap-3 mt-4">
                    <button
                        disabled={pagination.current_page === 1}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition ${pagination.current_page === 1 ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                    >
                        Précédent
                    </button>
                    <span className="text-color11 font-medium text-sm">
                        {pagination.current_page} / {pagination.last_page}
                    </span>
                    <button
                        disabled={pagination.current_page === pagination.last_page}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition ${pagination.current_page === pagination.last_page ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                    >
                        Suivant
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CommissionsInfluenceur;
